import { z } from "zod";
import { RECOMMENDATION_COMPONENTS } from "../types/recommendation";

/**
 * The legal shape of a recommendation's frontmatter — the user-side surface.
 * One small header-only file per suggestion under
 * `shared/recommendations/<surface>/`; the recommender ranks these, the
 * frontend renders them as chips, and a click resolves here.
 *
 * Resolution: every click resolves to a tool call (or nothing).
 *   - `skill: <id>` — sugar for the most common call, `load_skill`: the
 *     backend hard-executes it by injecting the SOP before the agent's turn.
 *   - `tool: <name>` — fulfill via that tool (e.g. `faq`; future: `deploy`).
 *   - neither — a prompt: the chip text is the message.
 *
 * Recommendations and skills are N:M — one skill can back many phrasings;
 * never duplicate a skill to get a second chip.
 */

/** The agent surfaces a recommendation can be offered on. `homepage` is the
 * major-initiatives surface (what playbooks used to model); an entry can list
 * several, e.g. `[homepage, website]` for one shared across both. */
export const SURFACES = ["brainstorm", "website", "ads", "homepage"] as const;
const SurfaceEnum = z.enum(SURFACES);

/**
 * The pages within a surface (today: the ads flow's substeps — mirrors
 * Workflow.AdsSubstepNames). An entry with `stages` is offered only on
 * those pages; without it, anywhere on its surface.
 */
export const STAGES = ["content", "highlights", "keywords", "settings", "launch", "review"] as const;
const StageEnum = z.enum(STAGES);

/**
 * A subfield of a `group` field — one column of a repeatable record
 * (an FAQ's question, a pricing tier's price). Text-only: load-bearing
 * media inside records stays a top-level image field (or a bespoke
 * component, like Testimonials' headshots).
 */
export const collectsSubfield = z
  .object({
    field: z.string().min(1),
    prompt: z.string().min(1),
    placeholder: z.string().optional(),
    optional: z.boolean().optional(),
  })
  .strict();

/**
 * An input a clicked recommendation gathers before sending. The entry owns
 * its conversational copy: `prompt` is the question the input asks and
 * `placeholder` shows the usual scenarios. A bare string is shorthand for
 * { field }, with a "?" suffix marking optional.
 */
export const collectsField = z
  .object({
    field: z.string().min(1),
    prompt: z.string().min(1),
    placeholder: z.string().optional(),
    optional: z.boolean().optional(),
    // `accepts: image` → a drop square (shared upload pipeline);
    // `accepts: choice` → a segmented control over `options`; default text.
    // `multiple` → collects a list (a gallery's images). The collected value
    // is a string (the text / uploaded URL / picked option), or a string[]
    // when `multiple`.
    accepts: z.enum(["text", "image", "choice"]).optional(),
    multiple: z.boolean().optional(),
    // `choice` fields: the segmented control's options, and the one
    // preselected on open (so an untouched panel still submits it).
    options: z.array(z.string().min(1)).optional(),
    default: z.string().optional(),
    // `group` kind: declaring `fields` makes this a repeatable record — the
    // generic panel renders + add / − remove cards. Projects into
    // `collected` as an array of records.
    fields: z.array(collectsSubfield).min(1).optional(),
    // What one record is called — drives "+ Add <item_label>".
    item_label: z.string().min(1).optional(),
  })
  .strict()
  .refine((f) => (f.accepts === "choice") === (f.options !== undefined && f.options.length > 0), {
    message: "accepts:choice must declare options (and only choice fields may)",
  })
  .refine((f) => f.default === undefined || (f.options ?? []).includes(f.default), {
    message: "default must be one of options",
  })
  .refine(
    (f) =>
      f.fields === undefined ||
      (f.accepts === undefined && !f.multiple && f.placeholder === undefined),
    { message: "a group field declares only fields/item_label (no accepts/multiple/placeholder)" },
  )
  .refine((f) => f.item_label === undefined || f.fields !== undefined, {
    message: "item_label only applies to group fields",
  });

export const recommendationEntry = z
  .object({
    availability: z.array(SurfaceEnum), // agent surfaces; [] = not ready
    // Offered only on these pages of the surface; omit = anywhere on it.
    stages: z.array(StageEnum).optional(),
    status: z.enum(["available", "partial", "needs-build"]),
    // Chip icon name (lucide kebab-case, e.g. "image", "bar-chart-3"); the
    // frontend maps it to a rendered icon, falling back to a plus.
    icon: z.string().optional(),
    // Pinned ordering on the chips row: lower shows earlier. Unpinned
    // entries follow, short-first. Cross-surface: a pinned entry leads on
    // every surface it's offered.
    priority: z.number().int().positive().optional(),
    when: z.string().min(1), // when to suggest it (authoring guidance)
    suggested_text: z.string().min(1), // the chip label (and panel title)
    // Panel-header subhead under the title ("Use this if you want to link
    // to an external site."). Panel-opening entries should declare one.
    description: z.string().min(1).optional(),
    // The full engineered prompt a prompt chip seeds into the composer —
    // first-person, compelling, and worth reading: it both does the
    // prompting for the user and teaches them what a strong ask looks
    // like. Omit and the chip label is the seed.
    prompt: z.string().min(1).optional(),
    collects: z.array(z.union([z.string(), collectsField])).optional(),
    component: z.string().nullable().default(null), // null = prompt/simple-input
    skill: z.string().optional(), // resolution sugar: load_skill this SOP
    tool: z.string().optional(), // resolution: fulfill via this tool
    // Marks a recommendation whose fulfillment is a multi-step journey
    // (e.g. a fake-door demand test). Informational today — playbook
    // surfacing/re-entry is gated on Memories (see plan Decisions).
    playbook: z.boolean().optional(),
    updated_at: z.coerce.date().optional(),
  })
  .strict()
  .refine((e) => !(e.skill && e.tool), {
    message: "a recommendation declares at most one resolution (skill or tool)",
  })
  .refine((e) => (e.status === "needs-build") === (e.availability.length === 0), {
    message: "status:needs-build must hold exactly when availability is []",
  })
  // `prompt` seeds the composer ONLY for prompt chips (no collects, no
  // component). Panel chips submit via buildRecommendationSubmission, which
  // never reads `prompt` — so a prompt here is a silent dead field. Reject it
  // rather than let an author believe it does something.
  .refine((e) => !(e.prompt && ((e.collects && e.collects.length > 0) || e.component)), {
    message: "`prompt` only seeds prompt chips — a chip with `collects` or a `component` ignores it",
  })
  // A `component` the frontend can't render is a broken chip. Pin it to the
  // registered renderers here (in the schema both stacks share) so a typo
  // fails at authoring/commit, not at render time.
  .refine(
    (e) => e.component == null || (RECOMMENDATION_COMPONENTS as readonly string[]).includes(e.component),
    { message: `component must be null or one of: ${RECOMMENDATION_COMPONENTS.join(", ")}` }
  );

export type RecommendationEntryHeader = z.infer<typeof recommendationEntry>;
